import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

const DOC_TYPE_MAP = {
  dni: 'D', nie: 'N', pasaporte: 'P', residencia: 'I',
};
const DOC_TYPE_LABEL = {
  dni: 'DNI', nie: 'NIE', pasaporte: 'Pasaporte', residencia: 'Residencia',
};

export default function AdminHospederias() {
  const { user } = useAuth();
  const token = localStorage.getItem('aldealix_token');
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [viajeros, setViajeros] = useState([]);
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);

  // Camera
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraViajeroIdx, setCameraViajeroIdx] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [photos, setPhotos] = useState({}); // idx → dataURL

  // Load events
  useEffect(() => {
    (async () => {
      try {
        const data = await api.getAdminEvents(token);
        setEvents(data.events || data || []);
      } catch { }
      setLoadingEvents(false);
    })();
  }, [token]);

  // Load viajeros for selected event
  const loadViajeros = useCallback(async () => {
    if (!selectedEvent) return;
    setLoading(true);
    try {
      const data = await api.getHospederias(token, selectedEvent);
      setViajeros(data.viajeros || []);
      setEventData(data.event || null);
    } catch (e) {
      alert('Error cargando viajeros: ' + e.message);
    }
    setLoading(false);
  }, [selectedEvent, token]);

  useEffect(() => { loadViajeros(); }, [loadViajeros]);

  // Camera functions
  async function openCamera(idx) {
    setCameraViajeroIdx(idx);
    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (e) {
      alert('No se pudo acceder a la camara: ' + e.message);
      setCameraOpen(false);
    }
  }

  function capturePhoto() {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setPhotos(prev => ({ ...prev, [cameraViajeroIdx]: dataUrl }));
    closeCamera();
  }

  function closeCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
    setCameraViajeroIdx(null);
  }

  // Export hospederias
  function exportCSV() {
    if (viajeros.length === 0) return;

    const header = [
      'Tipo Doc.', 'Num. Documento', 'Soporte', 'Fecha Expedicion',
      'Nombre', 'Apellido 1', 'Apellido 2',
      'Sexo', 'Fecha Nacimiento', 'Nacionalidad',
      'Fecha Entrada', 'Fecha Salida',
      'Direccion', 'Municipio', 'Provincia',
    ];

    const formatDate = (d) => {
      if (!d) return '';
      // Handle Firestore Timestamp objects
      const date = d._seconds ? new Date(d._seconds * 1000) : new Date(d);
      if (isNaN(date.getTime())) return '';
      return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    };

    const rows = viajeros.map(v => [
      DOC_TYPE_LABEL[v.docType] || v.docType || '',
      v.docNum || '',
      v.docSupport || '',
      formatDate(v.docEmission),
      v.firstName || '',
      v.lastName1 || '',
      v.lastName2 || '',
      v.sex === 'M' ? 'Masculino' : v.sex === 'F' ? 'Femenino' : v.sex || '',
      v.birthYear ? `01/01/${v.birthYear}` : '',
      v.country || 'España',
      formatDate(v.fechaEntrada),
      formatDate(v.fechaSalida),
      v.address || '',
      v.municipality || '',
      v.province || '',
    ]);

    const csv = [header, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hospederias_${eventData?.name?.replace(/\s/g, '_') || selectedEvent}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const missingDocs = viajeros.filter(v => !v.docNum);

  return (
    <div style={{ padding: '2rem 2.5rem', maxWidth: 1200 }}>
      <h1 style={{ marginBottom: 8 }}>Hospederias</h1>
      <p style={{ color: 'var(--text-soft)', fontSize: '.85rem', marginBottom: '1.5rem' }}>
        Viajeros mayores de 14 años — Parte de entrada para la Guardia Civil
      </p>

      {/* Event selector */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ fontSize: '.75rem', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 6 }}>
          Selecciona un evento
        </label>
        <select
          value={selectedEvent}
          onChange={e => setSelectedEvent(e.target.value)}
          disabled={loadingEvents}
          style={{
            background: 'var(--surface2)', color: 'var(--white)', border: 'var(--border)',
            borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: '.9rem',
            width: '100%', maxWidth: 400, outline: 'none',
          }}
        >
          <option value="">{loadingEvents ? 'Cargando eventos...' : '— Selecciona evento —'}</option>
          {events.map(ev => (
            <option key={ev.id} value={ev.id}>
              {ev.name} ({ev.date ? new Date(ev.date._seconds ? ev.date._seconds * 1000 : ev.date).toLocaleDateString('es-ES') : '—'})
            </option>
          ))}
        </select>
      </div>

      {loading && <p style={{ color: 'var(--text-soft)' }}>Cargando viajeros...</p>}

      {/* Stats */}
      {!loading && selectedEvent && (
        <>
          <div style={{ display: 'flex', gap: 16, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{ background: 'var(--surface2)', border: 'var(--border)', borderRadius: 'var(--radius)', padding: '16px 24px' }}>
              <div style={{ fontSize: '.7rem', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Total viajeros 14+</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Fraunces, serif' }}>{viajeros.length}</div>
            </div>
            <div style={{ background: 'var(--surface2)', border: 'var(--border)', borderRadius: 'var(--radius)', padding: '16px 24px' }}>
              <div style={{ fontSize: '.7rem', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Con documento</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Fraunces, serif', color: 'var(--sage)' }}>{viajeros.length - missingDocs.length}</div>
            </div>
            {missingDocs.length > 0 && (
              <div style={{ background: 'var(--danger-dim)', border: '1px solid rgba(214,60,42,0.3)', borderRadius: 'var(--radius)', padding: '16px 24px' }}>
                <div style={{ fontSize: '.7rem', color: 'var(--danger)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Sin documento</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Fraunces, serif', color: 'var(--danger)' }}>{missingDocs.length}</div>
              </div>
            )}
            <div style={{ background: 'var(--surface2)', border: 'var(--border)', borderRadius: 'var(--radius)', padding: '16px 24px' }}>
              <div style={{ fontSize: '.7rem', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Fotos DNI</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Fraunces, serif', color: 'var(--sky)' }}>{Object.keys(photos).length}</div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, marginBottom: '1.5rem' }}>
            <button
              onClick={exportCSV}
              disabled={viajeros.length === 0}
              style={{
                background: 'var(--fern)', color: 'var(--white)', border: 'none',
                borderRadius: 'var(--radius)', padding: '10px 20px', fontSize: '.85rem',
                cursor: 'pointer', fontWeight: 500, opacity: viajeros.length === 0 ? 0.5 : 1,
              }}
            >
              Exportar CSV Hospederias
            </button>
          </div>

          {/* Table */}
          {viajeros.length === 0 ? (
            <p style={{ color: 'var(--text-soft)' }}>No hay viajeros mayores de 14 años en este evento.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%', borderCollapse: 'collapse', fontSize: '.8rem',
                background: 'var(--surface)', borderRadius: 'var(--radius)', overflow: 'hidden',
              }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(122,171,138,0.2)' }}>
                    {['', 'Nombre', 'Documento', 'Num. Documento', 'Soporte', 'Sexo', 'Nacimiento', 'Nacionalidad', 'Foto DNI'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--text-soft)', fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 500 }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {viajeros.map((v, i) => {
                    const hasDoc = !!v.docNum;
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(122,171,138,0.08)' }}>
                        <td style={{ padding: '8px 12px', fontSize: '.75rem', color: 'var(--text-soft)' }}>{i + 1}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                          {v.firstName} {v.lastName1} {v.lastName2}
                          {v.age && <span style={{ color: 'var(--text-soft)', fontSize: '.7rem', marginLeft: 6 }}>{v.age} años</span>}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          {hasDoc ? (
                            <span style={{ background: 'var(--green-pale)', color: 'var(--sage)', padding: '2px 8px', borderRadius: 4, fontSize: '.7rem' }}>
                              {DOC_TYPE_LABEL[v.docType] || v.docType}
                            </span>
                          ) : (
                            <span style={{ background: 'var(--danger-dim)', color: 'var(--danger)', padding: '2px 8px', borderRadius: 4, fontSize: '.7rem' }}>
                              Sin doc.
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', letterSpacing: '.05em' }}>{v.docNum || '—'}</td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '.75rem' }}>{v.docSupport || '—'}</td>
                        <td style={{ padding: '8px 12px' }}>{v.sex === 'M' ? 'H' : v.sex === 'F' ? 'M' : '—'}</td>
                        <td style={{ padding: '8px 12px' }}>{v.birthYear || '—'}</td>
                        <td style={{ padding: '8px 12px' }}>{v.country || '—'}</td>
                        <td style={{ padding: '8px 12px' }}>
                          {photos[i] ? (
                            <img
                              src={photos[i]}
                              alt="DNI"
                              onClick={() => openCamera(i)}
                              style={{ width: 60, height: 38, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', border: '2px solid var(--fern)' }}
                            />
                          ) : (
                            <button
                              onClick={() => openCamera(i)}
                              style={{
                                background: 'var(--surface3)', color: 'var(--text-soft)', border: '1px dashed rgba(122,171,138,0.3)',
                                borderRadius: 6, padding: '6px 12px', fontSize: '.7rem', cursor: 'pointer',
                              }}
                            >
                              Foto
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Camera modal */}
      {cameraOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.85)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
        }}>
          <p style={{ color: 'var(--white)', fontSize: '.85rem' }}>
            Enfoca el DNI del viajero {cameraViajeroIdx !== null ? viajeros[cameraViajeroIdx]?.firstName + ' ' + viajeros[cameraViajeroIdx]?.lastName1 : ''}
          </p>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ maxWidth: '90vw', maxHeight: '60vh', borderRadius: 8, border: '2px solid var(--fern)' }}
          />
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={capturePhoto}
              style={{
                background: 'var(--fern)', color: 'var(--white)', border: 'none',
                borderRadius: 'var(--radius)', padding: '12px 32px', fontSize: '.9rem',
                cursor: 'pointer', fontWeight: 600,
              }}
            >
              Capturar
            </button>
            <button
              onClick={closeCamera}
              style={{
                background: 'var(--surface3)', color: 'var(--white)', border: 'var(--border)',
                borderRadius: 'var(--radius)', padding: '12px 24px', fontSize: '.9rem',
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
